import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { centsToMajorString, majorStringToCents } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { SecretInput } from '@/shared/ui/secret-input';

interface PriceOverrideDialogProps {
  line_item_name: string;
  line_quantity: string;
  current_unit_price_cents: string;
  submitting: boolean;
  error_text: string | null;
  on_close: () => void;
  on_submit: (args: {
    override_unit_price_cents: string;
    reason: string;
    otp_plaintext: string;
  }) => void | Promise<void>;
}

/**
 * Doctor-facing dialog to request an OTP-authorized line price override.
 * The doctor types the requested new price + reason + an OTP plaintext from
 * the admin-distributed pack. Backend burns the OTP and stamps the override
 * snapshot on the sale line (Stage A).
 */
export function PriceOverrideDialog({
  line_item_name,
  line_quantity,
  current_unit_price_cents,
  submitting,
  error_text,
  on_close,
  on_submit,
}: PriceOverrideDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [new_price_major, setNewPriceMajor] = useState<string>(
    centsToMajorString(current_unit_price_cents),
  );
  const [reason, setReason] = useState<string>('');
  const [otp, setOtp] = useState<string>('');
  const [local_error, setLocalError] = useState<string | null>(null);

  const can_submit =
    !submitting &&
    reason.trim().length > 0 &&
    otp.trim().length > 0 &&
    /^\d+(\.\d{1,2})?$/.test(new_price_major.trim());

  const onClickSubmit = async (): Promise<void> => {
    if (!can_submit) return;
    let override_cents: number;
    try {
      override_cents = majorStringToCents(new_price_major);
    } catch {
      setLocalError(t('doctor_pos.price_override_invalid_amount'));
      return;
    }
    if (override_cents < 0) {
      setLocalError(t('doctor_pos.price_override_invalid_amount'));
      return;
    }
    setLocalError(null);
    await on_submit({
      override_unit_price_cents: String(override_cents),
      reason: reason.trim(),
      otp_plaintext: otp.trim(),
    });
  };

  const error = local_error ?? error_text;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('doctor_pos.price_override_title')}</CardTitle>
          <p className="text-xs text-muted-foreground">
            {t('doctor_pos.price_override_description')}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
            <p className="font-medium">{line_item_name}</p>
            <p className="text-xs text-muted-foreground">
              {t('doctor_pos.price_override_line_summary', {
                qty: line_quantity,
                current: centsToMajorString(current_unit_price_cents),
              })}
            </p>
          </div>
          <div className="space-y-1">
            <Label htmlFor="po-new-price">{t('doctor_pos.price_override_new_price')}</Label>
            <Input
              id="po-new-price"
              type="text"
              inputMode="decimal"
              value={new_price_major}
              onChange={(e) => setNewPriceMajor(e.target.value)}
              disabled={submitting}
              placeholder="0.00"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="po-reason">{t('doctor_pos.price_override_reason')}</Label>
            <Input
              id="po-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              maxLength={255}
              placeholder={t('doctor_pos.price_override_reason_placeholder')}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="po-otp">{t('doctor_pos.price_override_otp')}</Label>
            <SecretInput
              id="po-otp"
              autoComplete="off"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              disabled={submitting}
              placeholder={t('otp.code_placeholder')}
            />
            <p className="text-xs text-muted-foreground">
              {t('doctor_pos.price_override_otp_hint')}
            </p>
          </div>
          {error ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              {error}
            </div>
          ) : null}
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {submitting ? t('common.loading') : t('doctor_pos.price_override_confirm')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
